import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { shiftService, stationPreferenceService } from '../../../services/api';
import {
    C, fmtDate,
    cardStyle, cardHeaderStyle, cardTitleStyle, labelStyle, inputStyle,
    primaryButtonStyle, secondaryButtonStyle, linkButtonStyle,
} from './shared';

const STATION_STYLES = {
    Kitchen: { bg: '#fffbeb', border: '#d97706', text: '#92400e', dot: '#d97706' },
    Bar:     { bg: '#eff6ff', border: '#2563eb', text: '#1e3a8a', dot: '#2563eb' },
    Cashier: { bg: '#eff5ff', border: '#2563eb', text: '#1e3a8a', dot: '#2563eb' },
    FOH:     { bg: '#f5f3ff', border: '#7c3aed', text: '#4c1d95', dot: '#7c3aed' },
};
const DEFAULT_SS = { bg: '#ecfeff', border: '#0891b2', text: '#164e63', dot: '#0891b2' };
const ss = (station) => STATION_STYLES[station] || DEFAULT_SS;
const STATIONS = ['Kitchen', 'Bar', 'Cashier', 'FOH'];
const OVERTIME = 40;
const DAY_NAMES = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

function toLocal(d) {
    return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
}

function getWeekRange() {
    const now = new Date();
    const day = now.getDay();
    const diff = day === 0 ? -6 : 1 - day;
    const mon = new Date(now); mon.setDate(now.getDate() + diff);
    const sun = new Date(mon); sun.setDate(mon.getDate() + 6);
    const dates = [];
    for (let i = 0; i < 7; i++) {
        const d = new Date(mon); d.setDate(mon.getDate() + i);
        dates.push(d);
    }
    return { start: toLocal(mon), end: toLocal(sun), dates };
}

function calcHours(s, e) {
    if (!s || !e) return 0;
    const [sh, sm] = s.split(':').map(Number);
    const [eh, em] = e.split(':').map(Number);
    let m = (eh * 60 + em) - (sh * 60 + sm);
    if (m === 0) return 0;
    if (m < 0) m += 1440;
    return Math.round(m / 30) * 0.5;
}

function fmtTime(t) {
    if (!t) return '—';
    return t.slice(0, 5);
}

const TabSchedule = ({ employee, t }) => {
    const navigate = useNavigate();
    const employeeId = employee?.employee_id;
    const [allShifts, setAllShifts] = useState([]);
    const [hoursData, setHoursData] = useState(null);
    const [prefs, setPrefs] = useState({ primary_station: null, secondary_station: null });
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(false);
    const [loading, setLoading] = useState(true);

    const { start: weekStart, end: weekEnd, dates: weekDates } = getWeekRange();
    const todayIso = toLocal(new Date());

    useEffect(() => {
        if (!employeeId) return;
        setLoading(true);
        const past = new Date(); past.setDate(past.getDate() - 14);
        const future = new Date(); future.setDate(future.getDate() + 14);

        Promise.all([
            shiftService.getShiftsByDateRange(toLocal(past), toLocal(future)).catch(() => []),
            shiftService.getEmployeeHours(employeeId, weekStart, weekEnd).catch(() => null),
            stationPreferenceService.get(employeeId).catch(() => null),
        ]).then(([shifts, hours, prefData]) => {
            setAllShifts((shifts || []).filter(s => s.employee_id === employeeId));
            setHoursData(hours);
            if (prefData) setPrefs({ primary_station: prefData.primary_station, secondary_station: prefData.secondary_station });
        }).finally(() => setLoading(false));
    }, [employeeId, weekStart, weekEnd]);

    const upcomingShifts = allShifts.filter(s => s.date >= todayIso).sort((a, b) => a.date.localeCompare(b.date)).slice(0, 7);
    const recentShifts = allShifts
        .filter(s => s.date < todayIso && (s.status === 'Completed' || s.actual_hours != null))
        .sort((a, b) => b.date.localeCompare(a.date))
        .slice(0, 7);

    // Mini week grid data
    const weekIsos = weekDates.map(d => toLocal(d));
    const shiftsByDay = {};
    weekIsos.forEach(iso => { shiftsByDay[iso] = []; });
    allShifts.forEach(s => { if (shiftsByDay[s.date]) shiftsByDay[s.date].push(s); });

    const handleSavePrefs = async () => {
        setSaving(true); setSaved(false);
        try {
            await stationPreferenceService.set(employeeId, {
                primary_station: prefs.primary_station || null,
                secondary_station: prefs.secondary_station || null,
            });
            setSaved(true);
            setTimeout(() => setSaved(false), 2500);
        } catch {} finally { setSaving(false); }
    };

    const totalHours = Number(hoursData?.total_hours || 0);
    const pct = Math.min((totalHours / OVERTIME) * 100, 100);
    const barColor = pct >= 100 ? C.red : pct >= 90 ? '#d97706' : C.green;

    if (loading) return <div style={{ padding: 20, color: C.muted, fontSize: 13 }}>{t('loading')}</div>;

    const shiftRow = (shift, showActual) => {
        const s = ss(shift.station);
        const actual = shift.actual_hours != null ? shift.actual_hours : null;
        const scheduled = calcHours(shift.start_time, shift.end_time);
        return (
            <div key={shift.shift_id} style={{
                display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px',
                borderRadius: 6, background: s.bg, border: `1px solid ${s.border}`,
            }}>
                <span style={{ width: 6, height: 6, borderRadius: '50%', background: s.dot, flexShrink: 0 }} />
                <span style={{ fontSize: 11, fontWeight: 700, color: s.text, minWidth: 55 }}>{shift.station || '—'}</span>
                <span style={{ fontSize: 11, color: C.text, flex: 1 }}>
                    {fmtTime(shift.start_time)} - {fmtTime(shift.end_time)}
                </span>
                {showActual && actual != null ? (
                    <span style={{ fontSize: 11, fontWeight: 700, color: C.green }}>{actual}h</span>
                ) : (
                    <span style={{ fontSize: 10, color: C.muted }}>{scheduled}h</span>
                )}
                {shift.status === 'In Progress' && (
                    <span style={{ fontSize: 9, fontWeight: 700, color: '#d97706', textTransform: 'uppercase' }}>Active</span>
                )}
            </div>
        );
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>

            {/* Row 1: Hours This Week — full width, compact */}
            <div style={cardStyle}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                    <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                            <h3 style={cardTitleStyle}>{t('hours_this_week')}</h3>
                            <span style={{ fontSize: 11, color: C.mutedLight }}>{fmtDate(weekStart)} – {fmtDate(weekEnd)}</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginBottom: 8 }}>
                            <span style={{ fontSize: 26, fontWeight: 800, color: barColor }}>{totalHours.toFixed(1)}h</span>
                            <span style={{ fontSize: 13, color: C.muted }}>/ {OVERTIME}h</span>
                            <span style={{ fontSize: 11, color: C.muted, marginLeft: 'auto' }}>
                                {pct >= 100
                                    ? `${(totalHours - OVERTIME).toFixed(1)}h overtime`
                                    : `${(OVERTIME - totalHours).toFixed(1)}h remaining`}
                            </span>
                        </div>
                        <div style={{ height: 6, borderRadius: 3, background: C.border, overflow: 'hidden' }}>
                            <div style={{ height: '100%', width: `${pct}%`, borderRadius: 3, background: barColor, transition: 'width 0.3s' }} />
                        </div>
                    </div>
                </div>
            </div>

            {/* Row 2: Station Preferences — compact inline */}
            <div style={cardStyle}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: 14, alignItems: 'end' }}>
                    <div>
                        <label style={labelStyle}>{t('primary_station')}</label>
                        <select
                            style={{ ...inputStyle, cursor: 'pointer' }}
                            value={prefs.primary_station || ''}
                            onChange={(e) => setPrefs(p => ({ ...p, primary_station: e.target.value || null }))}
                        >
                            <option value="">None</option>
                            {STATIONS.map(s => <option key={s} value={s}>{s}</option>)}
                        </select>
                    </div>
                    <div>
                        <label style={labelStyle}>{t('secondary_station')}</label>
                        <select
                            style={{ ...inputStyle, cursor: 'pointer' }}
                            value={prefs.secondary_station || ''}
                            onChange={(e) => setPrefs(p => ({ ...p, secondary_station: e.target.value || null }))}
                        >
                            <option value="">None</option>
                            {STATIONS.map(s => <option key={s} value={s}>{s}</option>)}
                        </select>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <button onClick={handleSavePrefs} disabled={saving} style={{ ...primaryButtonStyle, opacity: saving ? 0.7 : 1 }}>
                            {saving ? '...' : t('save')}
                        </button>
                        {saved && <span style={{ fontSize: 11, color: C.green, fontWeight: 600 }}>Saved</span>}
                    </div>
                </div>
            </div>

            {/* Row 3: Upcoming + Recent side by side */}
            <div className="portal-grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>

                {/* Upcoming Shifts (max 5) */}
                <div style={cardStyle}>
                    <div style={cardHeaderStyle}>
                        <h3 style={cardTitleStyle}>{t('upcoming_shifts')}</h3>
                        <button onClick={() => navigate(`/portal/schedule?employee=${employeeId}`)} style={linkButtonStyle}>
                            {t('view_full_schedule')}
                        </button>
                    </div>
                    {upcomingShifts.length === 0 ? (
                        <div style={{ padding: '16px 0', textAlign: 'center', fontSize: 12, color: C.mutedLight }}>
                            {t('no_upcoming_shifts')}
                        </div>
                    ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                            {upcomingShifts.slice(0, 5).map(shift => (
                                <div key={shift.shift_id} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                    <span style={{ fontSize: 10, fontWeight: 600, color: C.mutedLight, minWidth: 38 }}>
                                        {new Date(shift.date + 'T00:00').toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                                    </span>
                                    <div style={{ flex: 1 }}>{shiftRow(shift, false)}</div>
                                </div>
                            ))}
                            {upcomingShifts.length > 5 && (
                                <div style={{ textAlign: 'center', paddingTop: 4 }}>
                                    <button onClick={() => navigate(`/portal/schedule?employee=${employeeId}`)} style={{ ...linkButtonStyle, fontSize: 11 }}>
                                        +{upcomingShifts.length - 5} more
                                    </button>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Recent Worked Shifts */}
                <div style={cardStyle}>
                    <div style={cardHeaderStyle}>
                        <h3 style={cardTitleStyle}>{t('recent_shifts')}</h3>
                    </div>
                    {recentShifts.length === 0 ? (
                        <div style={{ padding: '16px 0', textAlign: 'center', fontSize: 12, color: C.mutedLight }}>
                            {t('no_recent_shifts')}
                        </div>
                    ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                            {recentShifts.slice(0, 5).map(shift => (
                                <div key={shift.shift_id} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                    <span style={{ fontSize: 10, fontWeight: 600, color: C.mutedLight, minWidth: 38 }}>
                                        {new Date(shift.date + 'T00:00').toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                                    </span>
                                    <div style={{ flex: 1 }}>{shiftRow(shift, true)}</div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

        </div>
    );
};

export default TabSchedule;
